import axios from 'axios';
import MainURL from '../../../MainURL';

export type ContractTypeId = 'web' | 'app' | 'maintenance' | 'consulting';

export type PaymentTerm = {
  category: string;
  amount: string;
  condition: string;
};

export type ApplicationSource = 'inquiry' | 'contract';

export type PublicContract = {
  id: number;
  // 2026-09-18: 신청(inquiries)~MVP확인 단계는 'inquiry', 전자서명(계약 체결) 이후는 'contract'.
  // 어느 액션 API를 호출할지(/inquiry/:id/... vs /api/contracts/:id/...) 이 값으로 분기한다.
  source: ApplicationSource;
  customer_name: string;
  phone: string;
  contract_type: ContractTypeId | string;
  contract_name: string | null;
  product_name: string | null;
  company_name: string | null;
  representative: string | null;
  business_number: string | null;
  email: string | null;
  address: string | null;
  build_cost: string | null;
  build_period: string | null;
  deposit_amount: number | null;
  maintenance_fee: string | null;
  maintenance_period: string | null;
  balance_amount: number | null;
  monthly_amount: number | null;
  authChurch: string | null;
  payment_terms: PaymentTerm[];
  special_notes: string | null;
  provider_company_name: string | null;
  provider_representative: string | null;
  provider_business_number: string | null;
  provider_contact: string | null;
  provider_address: string | null;
  provider_account_number: string | null;
  terms_text: string;
  status: string;
  signed_at?: string | null;
  terms_agreed?: boolean;
  privacy_agreed?: boolean;
  signature_name?: string | null;
  signature_image?: string | null;
  member_size: string | null;
  selected_member_tabs: string[];
  selected_admin_tabs: string[];
  sundayschool_addon: boolean;
  plan_type: PlanType | null;
  mvp_preview_url: string | null;
  mvp_note: string | null;
};

export type PlanType = 'subscription' | 'purchase';

type ApiErrorBody = {
  ok?: boolean;
  error?: { code?: string; message?: string };
  message?: string;
};

const API_BASE = `${MainURL}/api/contracts`;
const INQUIRY_BASE = `${MainURL}/inquiry`;

function getErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.error?.message) return body.error.message;
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

export async function lookupContract(customerName: string, phone: string) {
  try {
    const res = await axios.post<{ ok: boolean; data?: PublicContract }>(`${API_BASE}/lookup`, {
      customer_name: customerName,
      phone,
    });
    if (!res.data?.ok || !res.data.data) {
      throw new Error('계약 조회 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '계약 조회에 실패했습니다.'));
  }
}

export async function confirmDepositTransfer(
  id: number,
  source: ApplicationSource,
  payload: { customer_name: string; phone: string },
) {
  try {
    const base = source === 'inquiry' ? INQUIRY_BASE : API_BASE;
    const res = await axios.post<{ ok: boolean; data?: PublicContract }>(
      `${base}/${id}/confirm-deposit-transfer`,
      payload,
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('입금 확인 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '입금 확인 처리에 실패했습니다.'));
  }
}

export async function confirmBalanceTransfer(id: number, payload: { customer_name: string; phone: string }) {
  try {
    const res = await axios.post<{ ok: boolean; data?: PublicContract }>(
      `${API_BASE}/${id}/confirm-balance-transfer`,
      payload,
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('잔금 입금 확인 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '잔금 입금 확인 처리에 실패했습니다.'));
  }
}

// mvp_ready -> mvp_confirmed 전환은 항상 전자서명(계약 체결) 이전이라 inquiries 대상.
export async function confirmMvp(id: number, payload: { customer_name: string; phone: string }) {
  try {
    const res = await axios.post<{ ok: boolean; data?: PublicContract }>(
      `${INQUIRY_BASE}/${id}/confirm-mvp`,
      payload,
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('MVP 확인 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, 'MVP 확인 처리에 실패했습니다.'));
  }
}

export async function updateSelection(
  id: number,
  source: ApplicationSource,
  payload: {
    customer_name: string;
    phone: string;
    memberSize: string;
    apps: string[];
  },
) {
  try {
    const base = source === 'inquiry' ? INQUIRY_BASE : API_BASE;
    const res = await axios.post<{ ok: boolean; data?: PublicContract }>(
      `${base}/${id}/update-selection`,
      payload,
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('선택 항목 변경 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '선택 항목 변경에 실패했습니다.'));
  }
}

// 요금제 선택은 항상 전자서명 이전(mvp_confirmed) 단계라 inquiries 대상.
export async function selectPlan(
  id: number,
  payload: { customer_name: string; phone: string; planType: PlanType },
) {
  try {
    const res = await axios.post<{ ok: boolean; data?: PublicContract }>(
      `${INQUIRY_BASE}/${id}/select-plan`,
      payload,
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('요금제 선택 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '요금제 선택에 실패했습니다.'));
  }
}

// 전자서명(=계약 체결) - id는 inquiry id. 서버가 이 호출로 online_contracts row를 새로 만들고
// 응답의 id/source가 그 새 계약으로 바뀐다(이후 화면은 매번 고객명+전화번호로 재조회하므로
// 이 id를 별도로 들고 다닐 필요는 없다).
export async function signContract(
  id: number,
  payload: {
    customer_name: string;
    phone: string;
    terms_agreed: boolean;
    privacy_agreed: boolean;
    signature_name: string;
    signature_image: string;
  },
) {
  try {
    const res = await axios.post<{ ok: boolean; data?: PublicContract }>(
      `${INQUIRY_BASE}/${id}/sign`,
      payload,
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('계약 서명 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '계약 서명에 실패했습니다.'));
  }
}
